import { connect, FormikValues } from 'formik'
import { get, startCase } from 'lodash'
import { useEffect } from 'react'
import { IntlShape, useIntl } from 'react-intl'
import { useQueries, useQuery, UseQueryOptions } from 'react-query'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import { string } from 'yup'

import Accordion from '@/components/Accordion/Accordion'
import Loading from '@/components/Card/Loading'
import { Select } from '@/components/Form'
import { WorkgridDatasourceConfiguration } from '@/pages/SpaceAppBuilder/AppDetailsPage/interfaces'
import { PollingIntervalStep } from '@/pages/SpaceAppBuilder/components/DataSourceWizard/DataSourceWizard'
import VariableAutoComplete from '@/pages/SpaceAppBuilder/components/VariableInputDropdown'
import { getVariablesFromSourceObject } from '@/pages/SpaceAppBuilder/FeaturesPage/variableBuilder'
import {
  App,
  AppInstanceDataSource,
  DataSourceEventTriggerData,
  WorkgridCatalogDatasource
} from '@/pages/SpaceAppBuilder/utilities/interfaces'
import {
  dataSourceV3CatalogQueryOptions,
  getDataSourceConfigurationQueryOptions,
  getFetchAppQueryOptions
} from '@/pages/SpaceAppBuilder/utilities/queries'
import { StyledErrorContainer } from '@/pages/SpaceAppBuilder/utilities/StyledComponents'

import messages from './messages'

//<editor-fold desc="Interfaces">
type DataSourceEventTriggerType = {
  readOnly?: boolean
}
//</editor-fold>

//<editor-fold desc="Styled Components">
const StyledSelect = styled(Select).withConfig({
  shouldForwardProp: prop => prop !== 'isPlaceholder'
})`
  flex: 1 0 auto;
  margin: 0.5rem 0 1rem;
  .field__wrapper .field__input {
    color: ${props => (props.isPlaceholder ? 'gray' : '#000000')};
  }
`

const LoaderOverlay = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: rgba(243, 243, 244, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
`

const Description = styled.p`
  margin: -10px 0 20px 0;
  font-size: 0.9rem;
  color: #6c6c6c;
`

const StyledVariableAutoComplete = styled(VariableAutoComplete)`
  && {
    &.form__filter-select {
      .field__wrapper .ui.fluid.dropdown {
        min-height: 40px;
      }
    }
  }
`
const StyledAccordionMainTitle = styled.span`
  font-weight: bold;
  font-size: 1rem;
  color: #4b4b4b;
`
//</editor-fold>

const getValidationSchema = (intl: IntlShape) => {
  return {
    triggerSourceKey: string().required(intl.formatMessage(messages.pleaseSelectTriggerSource)),
    triggerEvent: string().required(intl.formatMessage(messages.pleaseSelectTriggerEvent))
  }
}

const getInitialValues = (triggerData: any): DataSourceEventTriggerData => {
  return {
    triggerSourceImageUrl: triggerData.triggerSourceImageUrl,
    triggerEvent: triggerData.triggerEvent,
    triggerSourceKey: triggerData.triggerSourceKey,
    pollingInterval: triggerData.pollingInterval ?? '5',
    watchedFields: triggerData.watchedFields || []
  }
}
const getDataSourceEventTriggerHelpUrl = 'https://help.workgrid.com/s/article/Data-Source-Trigger'
const WorkgridDataSourceEventTrigger = ({ formik, readOnly = false }: DataSourceEventTriggerType & FormikValues) => {
  const intl = useIntl()

  const { values, setFieldValue } = formik
  const { spaceId, appId } = useParams<{ spaceId: string; appId: string }>()

  const appQueryResult = useQuery<App>({
    ...getFetchAppQueryOptions({ spaceId, appId })
  })

  const dataSources: AppInstanceDataSource[] = appQueryResult?.data?.dataSources || []

  type WorkgridDatasourceConfigurationQueries = UseQueryOptions<WorkgridDatasourceConfiguration>[]
  const dataSourceConfigurationsQuery =
    useQueries<WorkgridDatasourceConfigurationQueries>(
      dataSources?.map((dataSource: AppInstanceDataSource) => ({
        ...dataSourceV3CatalogQueryOptions({
          spaceId,
          datasourceId: dataSource.datasourceId || dataSource?.key
        })
      }))
    ) ?? []

  const workgridDataSources = dataSourceConfigurationsQuery.reduce(
    (workgridDataSources: WorkgridDatasourceConfiguration[], dataSource) => {
      if (dataSource.data) {
        workgridDataSources.push(dataSource.data)
      }
      return workgridDataSources
    },
    []
  )

  const triggerDataSource = dataSources?.find(dataSource => dataSource.key === values.triggerData.triggerSourceKey)

  const { isLoading: isLoadingDataSourceConfiguration, data: dataSourceConfiguration } = useQuery<
    WorkgridCatalogDatasource,
    Error
  >({
    ...getDataSourceConfigurationQueryOptions({
      spaceId,
      dataSourceId: get(triggerDataSource, 'key', '')
    })
  })

  useEffect(() => {
    if (values.triggerData?.triggerSourceKey) {
      const filteredDataSources = workgridDataSources?.filter(
        dataSource => dataSource.id === values.triggerData.triggerSourceKey
      )
      if (filteredDataSources?.length) {
        setFieldValue('name', filteredDataSources[0].name)
      }
    }
  }, [setFieldValue, appQueryResult?.data?.dataSources, values.triggerData.triggerSourceKey])

  const getFieldObject = () => {
    if (!dataSourceConfiguration?.events[values.triggerData.triggerEvent]?.eventModel?.properties) {
      return []
    }
    return (
      Object.keys(dataSourceConfiguration?.events[values.triggerData.triggerEvent]?.eventModel?.properties)
        .sort()
        .map(dataSourceObjectKey => {
          return getVariablesFromSourceObject(
            dataSourceConfiguration?.events[values.triggerData.triggerEvent]?.eventModel?.properties[
              dataSourceObjectKey
            ],
            dataSourceObjectKey,
            values?.triggerData?.triggerSourceImageUrl
          )
        }) || []
    )
  }

  const getEvents = () => Object.keys(dataSourceConfiguration?.events || {})

  const DefaultOption = () => {
    const intl = useIntl()

    return (
      <option value={-1} selected disabled>
        {intl.formatMessage(messages.selectTriggerEvent)}
      </option>
    )
  }

  const pollingSliderSteps: PollingIntervalStep[] = [
    { label: intl.formatMessage(messages.polling5MinutesDisplay), value: '5' },
    { label: intl.formatMessage(messages.polling10MinutesDisplay), value: '10' },
    { label: intl.formatMessage(messages.polling15MinutesDisplay), value: '15' },
    { label: intl.formatMessage(messages.polling30MinutesDisplay), value: '30' },
    { label: intl.formatMessage(messages.polling1HourDisplay), value: '60' },
    { label: intl.formatMessage(messages.polling6HoursDisplay), value: '360' },
    { label: intl.formatMessage(messages.polling12HoursDisplay), value: '720' },
    { label: intl.formatMessage(messages.polling24HoursDisplay), value: '1440' }
  ]

  return triggerDataSource ? (
    <div className="px-2">
      <Description>{intl.formatMessage(messages.triggerSetup)}</Description>
      {isLoadingDataSourceConfiguration || !values.triggerData?.triggerSourceKey ? (
        <LoaderOverlay>
          <Loading isLoading={isLoadingDataSourceConfiguration} loadingMessage={intl.formatMessage(messages.loading)} />
        </LoaderOverlay>
      ) : (
        <>
          <StyledSelect
            disabled={readOnly}
            defaultValue={-1}
            key="triggerData.triggerEvent"
            name="triggerData.triggerEvent"
            isPlaceholder={!values.triggerData.triggerEvent}
            label={intl.formatMessage(messages.triggerEvent)}
            variant={''}
          >
            <>
              <DefaultOption />
              {getEvents().map((eventObject: string) => (
                <Select.Option key={eventObject} value={eventObject}>
                  {startCase(eventObject)}
                </Select.Option>
              ))}
            </>
          </StyledSelect>
          {!!formik.values.triggerData.triggerEvent && (
            <StyledVariableAutoComplete
              readOnly={readOnly}
              descriptionPosition={'top'}
              description="Feature will run when the specified fields are changed."
              id={'watched-fields-input'}
              name="triggerData.watchedFields"
              label={intl.formatMessage(messages.watchedFields, {
                triggerEvent: startCase(formik.values.triggerData.triggerEvent)
              })}
              variables={getFieldObject()}
              multiple={true}
              showVariableButton={false}
              placeholder={intl.formatMessage(messages.selectFields)}
              showValidationMessage={true}
            />
          )}
          {dataSourceConfiguration?.events[values.triggerData.triggerEvent]?.eventType === 'polling' && (
            <Accordion
              className="mt-2"
              defaultOpen={false}
              title={<StyledAccordionMainTitle>Advanced Settings</StyledAccordionMainTitle>}
            >
              <div>
                <StyledSelect
                  name={`triggerData.pollingInterval`}
                  label={'How often would you like to check for new/updated records?'}
                  variant={''}
                  options={pollingSliderSteps.map((step: any) => ({ value: step.value, label: step.label }))}
                  disabled={readOnly}
                />
              </div>
            </Accordion>
          )}
        </>
      )}
    </div>
  ) : (
    <StyledErrorContainer>{intl.formatMessage(messages.triggerDataSourceRemovedError)}</StyledErrorContainer>
  )
}
export default connect(WorkgridDataSourceEventTrigger)
export { getValidationSchema, getInitialValues, getDataSourceEventTriggerHelpUrl }
